import { getCurrencyCode } from '@/shared/lib/format/money';
import {
  DEFAULT_AGE_BUCKETS,
  EMPTY_TREND_POINT,
} from '../constants/totalAnalytics.constants';
import type {
  AgeDistributionItem,
  MonthWiseAnalyticsResponse,
  PeriodSummary,
  StatCardConfig,
  TrendPoint,
} from '../types/totalAnalytics.types';

export function formatMonthYearFromIso(iso?: string) {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
}

export function buildDateRangeLabel(dateRange?: MonthWiseAnalyticsResponse['dateRange']) {
  const start = formatMonthYearFromIso(dateRange?.startDate);
  const end = formatMonthYearFromIso(dateRange?.endDate);
  if (start && end) return `${start} - ${end}`;
  return '';
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatMonthLabel(item: Record<string, unknown>) {
  const rawMonth = item.month ?? item.monthKey ?? item.monthNum;
  const monthNum = Number(rawMonth);
  const yearNum = Number(item.year ?? '');

  if (Number.isFinite(monthNum) && monthNum >= 1 && monthNum <= 12) {
    const monthName = MONTH_NAMES[monthNum - 1];
    return Number.isFinite(yearNum) && yearNum > 0
      ? `${monthName} ${yearNum}`
      : monthName;
  }

  const rawName = String(
    item.monthName ||
      item.monthLabel ||
      item.name ||
      item.label ||
      item.month ||
      '',
  ).trim();

  if (rawName) return rawName;

  return '—';
}

export function extractMonthWiseList(
  monthWiseResponse?: MonthWiseAnalyticsResponse | Record<string, unknown>[] | unknown,
): Record<string, unknown>[] {
  if (!monthWiseResponse) return [];
  if (Array.isArray(monthWiseResponse)) return monthWiseResponse;
  if (typeof monthWiseResponse === 'object') {
    const res = monthWiseResponse as Record<string, unknown>;
    if (Array.isArray(res.monthWiseData)) return res.monthWiseData as Record<string, unknown>[];
    if (Array.isArray(res.data)) return res.data as Record<string, unknown>[];
    if (Array.isArray(res.campaigns)) return res.campaigns as Record<string, unknown>[];
    if (Array.isArray(res.items)) return res.items as Record<string, unknown>[];
    if (Array.isArray(res.list)) return res.list as Record<string, unknown>[];
  }
  return [];
}

export function getDefaultTrendsData(): TrendPoint[] {
  const points: TrendPoint[] = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = MONTH_NAMES[d.getMonth()];
    const year = d.getFullYear();
    points.push({
      name: `${monthName} ${year}`,
      total: 0,
      live: 0,
      published: 0,
      approved: 0,
      expired: 0,
      rejected: 0,
      pending: 0,
      cancelled: 0,
    });
  }
  return points;
}

export function mapMonthWiseToTrends(
  monthWiseInput?: MonthWiseAnalyticsResponse | Record<string, unknown>[] | unknown,
): TrendPoint[] {
  const rawList = extractMonthWiseList(monthWiseInput);

  if (!rawList.length) {
    return getDefaultTrendsData();
  }

  return rawList.map((item) => {
    const pending = Number(item.pending ?? 0);
    const published = Number(item.published ?? 0);
    const approved = Number(item.approved ?? 0);
    const live = Number(item.live ?? item.active ?? 0);
    const expired = Number(item.expired ?? 0);
    const rejected = Number(item.rejected ?? 0);
    const cancelled = Number(item.cancel ?? item.cancelled ?? 0);

    return {
      name: formatMonthLabel(item),
      total: Number(
        item.totalCampaigns ??
          item.total ??
          item.count ??
          pending + published + approved + live + expired + rejected + cancelled,
      ),
      pending,
      published,
      approved,
      live,
      expired,
      rejected,
      cancelled,
    };
  });
}

export function mapAgeDistribution(
  ageDistributionResponse: unknown,
): AgeDistributionItem[] {
  const list = (() => {
    if (Array.isArray(ageDistributionResponse)) return ageDistributionResponse;
    if (
      ageDistributionResponse &&
      typeof ageDistributionResponse === 'object'
    ) {
      const obj = ageDistributionResponse as Record<string, unknown>;
      if (Array.isArray(obj.data)) return obj.data;
      if (Array.isArray(obj.ageDistribution)) return obj.ageDistribution;
      if (Array.isArray(obj.items)) return obj.items;
      if (Array.isArray(obj.list)) return obj.list;
    }
    return null;
  })();

  if (!list || !list.length) {
    return [...DEFAULT_AGE_BUCKETS];
  }

  return list.map((item: Record<string, unknown>) => ({
    label: String(item.label || item.range || item.ageGroup || item.age || '—'),
    count: Number(item.count || item.total || item.views || 0),
  }));
}

export function buildPeriodSummary(
  monthWiseResponse: MonthWiseAnalyticsResponse | undefined,
  trendsData: TrendPoint[],
  dateRangeLabel: string,
): PeriodSummary {
  const firstPoint = trendsData[0];
  const lastPoint = trendsData[trendsData.length - 1] ?? { ...EMPTY_TREND_POINT };
  const dynamicRange =
    firstPoint && lastPoint && firstPoint !== lastPoint
      ? `${firstPoint.name} - ${lastPoint.name}`
      : lastPoint.name;

  const byStatus =
    monthWiseResponse?.summary?.byStatus ||
    (typeof monthWiseResponse === 'object' &&
      (monthWiseResponse as Record<string, unknown>)?.byStatus as
        | Record<string, number | undefined>
        | undefined);

  if (!byStatus) {
    const aggregated = trendsData.reduce(
      (acc, curr) => ({
        total: acc.total + curr.total,
        pending: acc.pending + curr.pending,
        published: acc.published + curr.published,
        approved: acc.approved + curr.approved,
        live: acc.live + curr.live,
        expired: acc.expired + curr.expired,
        rejected: acc.rejected + curr.rejected,
        cancelled: acc.cancelled + curr.cancelled,
      }),
      {
        total: 0,
        pending: 0,
        published: 0,
        approved: 0,
        live: 0,
        expired: 0,
        rejected: 0,
        cancelled: 0,
      },
    );

    return {
      name: dateRangeLabel || dynamicRange || 'All Time',
      ...aggregated,
    };
  }

  return {
    name: dateRangeLabel || dynamicRange || 'All Time',
    total: Number(
      monthWiseResponse?.summary?.totalCampaigns ??
        trendsData.reduce((sum, item) => sum + item.total, 0),
    ),
    pending: Number(byStatus.pending ?? 0),
    published: Number(byStatus.published ?? 0),
    approved: Number(byStatus.approved ?? 0),
    live: Number(byStatus.live ?? byStatus.active ?? 0),
    expired: Number(byStatus.expired ?? 0),
    rejected: Number(byStatus.rejected ?? 0),
    cancelled: Number(byStatus.cancel ?? byStatus.cancelled ?? 0),
  };
}

export function buildStatCards(
  statsData: Record<string, unknown> | undefined,
  monthWiseResponse: MonthWiseAnalyticsResponse | undefined,
): StatCardConfig[] {
  const campaignCount =
    monthWiseResponse?.summary?.totalCampaigns ??
    statsData?.totalCampaigns ??
    statsData?.campaignCount ??
    statsData?.campaigns ??
    statsData?.count ??
    0;

  const campaignCost = Number(
    monthWiseResponse?.summary?.totalRevenue ??
      statsData?.totalSpent ??
      statsData?.amountSpent ??
      statsData?.totalRevenue ??
      statsData?.spend ??
      statsData?.revenue ??
      0,
  );

  const totalViews =
    statsData?.totalViews ??
    statsData?.totalView ??
    statsData?.views ??
    statsData?.impressions ??
    statsData?.totalImpression ??
    0;

  const maleViews =
    statsData?.maleViews ??
    statsData?.maleView ??
    statsData?.maleCount ??
    0;

  const femaleViews =
    statsData?.femaleViews ??
    statsData?.femaleView ??
    statsData?.femaleCount ??
    0;

  const footfall =
    statsData?.footfallCount ??
    statsData?.footfall ??
    statsData?.footfall_count ??
    '0.00';

  const engagement =
    statsData?.engagementScore ??
    statsData?.engagement_score ??
    statsData?.engagement ??
    '0.00';

  const dwell =
    statsData?.dwellTime ??
    statsData?.dwell_time ??
    '0.00';

  return [
    {
      title: 'Campaign',
      value: String(campaignCount),
      iconKey: 'campaign',
      cardBgClass:
        'bg-[linear-gradient(135deg,#EEF4FF_0%,#E0ECFF_100%)] border border-blue-100',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Total active campaigns in the selected period.',
    },
    {
      title: 'Campaign Cost',
      value: `${getCurrencyCode()} ${campaignCost.toFixed(2)}`,
      iconKey: 'cost',
      cardBgClass:
        'bg-[linear-gradient(135deg,#ECFDF5_0%,#DDFBEF_100%)] border border-emerald-100',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Total cost for campaigns within the selected period.',
    },
    {
      title: 'Total Views',
      value: String(totalViews),
      iconKey: 'views',
      cardBgClass:
        'bg-[linear-gradient(135deg,#F3F4FF_0%,#E8EBFF_100%)] border border-indigo-100',
      iconBgClass: '',
      infoText: 'Total number of ad views (impressions).',
    },
    {
      title: 'Male Views',
      value: String(maleViews),
      iconKey: 'male',
      cardBgClass:
        'bg-[linear-gradient(135deg,#F8FAFC_0%,#EEF2F7_100%)] border border-slate-200',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Views from male audience segments.',
    },
    {
      title: 'Female Views',
      value: String(femaleViews),
      iconKey: 'female',
      cardBgClass:
        'bg-[linear-gradient(135deg,#FFF1F5_0%,#FFE4EC_100%)] border border-rose-100',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Views from female audience segments.',
    },
    {
      title: 'Footfall count',
      value: String(footfall),
      iconKey: 'footfall',
      cardBgClass:
        'bg-[linear-gradient(135deg,#ECFEFF_0%,#D7F7F1_100%)] border border-cyan-100',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Based on people passing by',
    },
    {
      title: 'Engagement Score',
      value: String(engagement),
      iconKey: 'engagement',
      cardBgClass:
        'bg-[linear-gradient(135deg,#FFF7ED_0%,#FFEDD5_100%)] border border-amber-100',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Based on visibility & interaction.',
    },
    {
      title: 'Dwell Time',
      value: String(dwell),
      iconKey: 'dwell',
      cardBgClass:
        'bg-[linear-gradient(135deg,#F0FDFA_0%,#DDFBF4_100%)] border border-teal-100',
      iconBgClass: 'bg-white/90 shadow-sm',
      infoText: 'Average duration users spend viewing content.',
    },
  ];
}
